/**
 * Edge Functions Service
 * Handles calls to Supabase Edge Functions with offline support
 */
import { getAuthenticatedClient, SUPABASE_URL, SUPABASE_ANON_KEY } from './client';
import { requestQueue } from './requestQueue';

interface EdgeFunctionOptions {
  body?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
}

interface EdgeFunctionResponse<T = any> {
  data: T | null;
  error: Error | null;
}

export interface VoiceOrchestratorResponse {
  success: boolean;
  transcription: { text: string; language: string } | null;
  response: { text: string; source: string } | null;
  tts: { audio_url: string; provider: string } | null;
  error_code?: 'NO_SPEECH' | 'LOW_CONFIDENCE' | 'QUOTA_EXCEEDED';
  error_audio_url?: string;
  conversation_type: string | null;
  points_earned: number | null;
  error: string | null;
  processing_time_ms: number;
}

/** upload-photo reply (FIX-CBS-B2-SERVER-CHAIN): the photo row plus the server-chain facts. */
export interface UploadPhotoReply {
  success?: boolean;
  duplicate?: boolean;
  error?: string;
  reason_code?: string;
  photo?: Record<string, unknown>;
  storagePath?: string;
  thumbnailPath?: string | null;
  activity_id?: string | null;
  points_awarded?: number;
  award_duplicate?: boolean | null;
  award?: 'ledger' | 'none';
  analysis?: 'started' | 'skipped';
  batch_id?: string | null;
  app?: string | null;
  chain_error?: string;
  server_now?: string;
}

class EdgeFunctionsService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = `${SUPABASE_URL}/functions/v1`;
  }

  private getAuthHeaders(): Record<string, string> {
    const sessionToken = this.getSessionToken();
    return {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      ...(sessionToken ? { 
        'Authorization': `Bearer ${sessionToken}`,
        'x-session-token': sessionToken,
      } : {}),
    };
  }

  private getSessionToken(): string | null {
    // FIX-CBS-B2-SERVER-CHAIN: the CBS app persists its session under 'agxl_session' (AuthContext
    // STORAGE_KEYS.session, localStorage or sessionStorage). This helper read a key nothing ever
    // wrote ('auth_session'), so every edge call left without x-session-token (process-photo
    // answered 401 on the old build — seen in e2e/b2-cbs/cbs-photo-network.mjs). The old key is
    // kept as a fallback.
    try {
      const stored = localStorage.getItem('agxl_session') || sessionStorage.getItem('agxl_session') || localStorage.getItem('auth_session');
      if (stored) {
        const session = JSON.parse(stored);
        return session.accessToken || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  async invoke<T = any>(
    functionName: string, 
    options: EdgeFunctionOptions = {}
  ): Promise<EdgeFunctionResponse<T>> {
    const url = `${this.baseUrl}/${functionName}`;
    const { body, headers = {}, timeout = 30000 } = options;

    // Check if offline - queue the request
    if (!navigator.onLine) {
      const queuedId = requestQueue.add({
        type: 'edge-function',
        functionName,
        body,
      });
      
      return {
        data: null,
        error: new Error(`Request queued (ID: ${queuedId}). Will retry when online.`),
      };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Edge function error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      clearTimeout(timeoutId);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  // AgriAgent chat function
  async agriAgentChat(userId: string, message: string, language: string, context?: Record<string, any>): Promise<EdgeFunctionResponse<{ response: { text: string; source: string }; suggestions?: string[] }>> {
    return this.invoke('agriagent-chat', {
      body: { user_id: userId, message, language, context },
    });
  }

  // Transcribe voice to text
  async transcribeVoice(audioBlob: Blob, language?: string, userId?: string): Promise<EdgeFunctionResponse<{ success: boolean; transcription?: { text: string; language: string }; error?: string }>> {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    const base64 = btoa(binary);

    return this.invoke('voice-transcribe', {
      body: { user_id: userId || undefined, audio_base64: base64, language: language || undefined },
    });
  }

  // Text to speech — returns base64-encoded MP3 audio from Google Cloud TTS
  async textToSpeech(text: string, language: string = 'en'): Promise<EdgeFunctionResponse<{ success: boolean; audio_base64: string; content_type: string }>> {
    return this.invoke('voice-tts', {
      body: { text, language },
    });
  }

  // Voice orchestrator — single STT + LLM + TTS pipeline. Owns voice_conversations writes.
  // Caller must upload audio to voice-messages bucket and insert the conversation row first,
  // then pass the conversation_id and audio_url here.
  async voiceOrchestrate(
    conversationId: string,
    audioUrl: string,
    language: string,
    userId: string,
  ): Promise<EdgeFunctionResponse<VoiceOrchestratorResponse>> {
    return this.invoke<VoiceOrchestratorResponse>('voice-orchestrator', {
      body: {
        conversation_id: conversationId,
        audio_url: audioUrl,
        language,
        user_id: userId,
      },
      timeout: 120000,
    });
  }

  // Get aggregated analytics
  async getAnalytics(userId: string, range: 'week' | 'month' | 'year' = 'month'): Promise<EdgeFunctionResponse<any>> {
    return this.invoke('get-analytics', {
      body: { userId, range },
    });
  }

  /** Process a daily poultry record with server-side validation and running totals. */
  async processDailyRecord(input: {
    batchId: string;
    date: string;
    feedKg: number;
    mortalityCount: number;
    waterLiters?: number;
    tempMin?: number;
    tempMax?: number;
    weightSamples?: { count: number; avgGrams: number };
    notes?: string;
  }): Promise<EdgeFunctionResponse<{ recordId: string; runningBirdCount: number; runningFcr: number; cumulativeFeedKg: number }>> {
    return this.invoke('process-daily-record', { body: input });
  }

  /** Query the peer-learning network for contextual farming advice. */
  async peerLearningSync(input: {
    queryText: string;
    cropId: string;
    countryId: string;
    batchDayContext?: number;
    shedTypeContext?: string;
    caller?: string;
  }): Promise<EdgeFunctionResponse<{ answers: Array<{ id: string; text: string; authorName: string; helpfulCount: number }>; totalResults: number }>> {
    return this.invoke('peer-learning-sync', { body: input });
  }

  /** Calculate end-of-cycle settlement based on harvest data. */
  async calculateSettlement(input: {
    batchId: string;
    harvestData: { totalBirds: number; avgWeightKg: number; pricePerKg: number; deductions?: Record<string, number> };
  }): Promise<EdgeFunctionResponse<{ settlementId: string; grossAmount: number; netAmount: number; deductions: Record<string, number>; currency: string }>> {
    return this.invoke('calculate-settlement', { body: input });
  }

  /** Process a payment disbursement for a completed settlement. */
  async processPayment(input: {
    settlementId: string;
    paymentProvider: string;
    accountNumber: string;
  }): Promise<EdgeFunctionResponse<{ transactionId: string; status: 'pending' | 'completed' | 'failed'; reference: string }>> {
    return this.invoke('process-payment', { body: input });
  }

  /** Admin/MAP: update a configuration record with audit trail. */
  async mapUpdateConfig(input: {
    table: string;
    recordId: string;
    updates: Record<string, any>;
    reason: string;
  }): Promise<EdgeFunctionResponse<{ success: boolean; auditLogId: string }>> {
    return this.invoke('map-update-config', { body: input });
  }

  /** Generate a downloadable report for an organisation. */
  async generateReport(input: {
    reportType: 'batch_summary' | 'credit_scores' | 'settlements' | 'activities';
    orgId: string;
    dateRange: { from: string; to: string };
    format?: 'pdf' | 'csv' | 'xlsx';
  }): Promise<EdgeFunctionResponse<{ reportUrl: string; expiresAt: string; rowCount: number }>> {
    return this.invoke('generate-report', { body: input });
  }

  /** Run fraud-detection checks on a batch's recorded data. */
  async detectFraudBatch(input: {
    batchId: string;
    modules?: Array<'mortality_spike' | 'fcr_anomaly' | 'weight_plateau' | 'photo_reuse'>;
  }): Promise<EdgeFunctionResponse<{ riskScore: number; flags: Array<{ module: string; severity: 'low' | 'medium' | 'high'; detail: string }>; recommendation: string }>> {
    return this.invoke('detect-fraud-batch', { body: input });
  }

  /**
   * FIX-CBS-B2-SERVER-CHAIN: the ONE photo call. Multipart, never queued (a photo taken offline is
   * refused by the server's kill switch today, so queuing it would only hide the refusal). The
   * session token rides in x-session-token exactly as for every other edge call.
   */
  async uploadPhoto(formData: FormData, timeout = 90000): Promise<{ ok: boolean; status: number; body: UploadPhotoReply | null }> {
    const url = `${this.baseUrl}/upload-photo`;
    const { 'Content-Type': _contentType, ...headers } = this.getAuthHeaders();
    void _contentType;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: formData, signal: controller.signal });
      let body: UploadPhotoReply | null = null;
      try { body = (await response.json()) as UploadPhotoReply; } catch { body = null; }
      return { ok: response.ok, status: response.status, body };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /** Trigger AI analysis on an uploaded photo (GPT-4o-mini vision). */
  async processPhoto(input: {
    user_id: string;
    photo_id: string;
    photo_category?: string;
    storage_path?: string;
    batch_id?: string;
  }): Promise<EdgeFunctionResponse<{
    success: boolean;
    photo_id: string;
    points_earned: number;
    ai_analysis: Record<string, unknown>;
    fraud_flags: string[] | null;
    processing_time_ms: number;
  }>> {
    return this.invoke('process-photo', { body: input });
  }
}

export const edgeFunctions = new EdgeFunctionsService();
